import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, X, Phone, Check, User, Trash2, UserPlus, Search, MapPin, Calendar, Clock, MessageSquare } from 'lucide-react';
import CustomDatePicker from './shared/CustomDatePicker';
import { useCart } from '../../customer/context/CartContext';
import { customerService } from '../../../services';
import { getAllUsers } from '../../../services/api/userApi';
import { getDeliverySchedules } from '../../../services/api/deliveryScheduleApi';

/**
 * Helper to determine default salesperson ID based on selected customer and logged in user
 */
const getDefaultSalesPersonId = (cust, user) => {
    if (cust?.assignedSalesPersons && cust.assignedSalesPersons.length > 0) {
        return cust.assignedSalesPersons[0].publicId || cust.assignedSalesPersons[0].id || '';
    }
    if (cust?.delegatedSalesPersons && cust.delegatedSalesPersons.length > 0) {
        return cust.delegatedSalesPersons[0].publicId || cust.delegatedSalesPersons[0].id || '';
    }
    return user?.publicId || user?.id || '';
};

/**
 * POS Cart Drawer
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onPlaceOrder: (cartItems, customer, assignedSalesPersonPublicId) => void
 *  - selectedCustomer: customer object (pre-selected from PosView) — optional
 */
const PosCartDrawer = ({ isOpen, onClose, onPlaceOrder, selectedCustomer: preSelectedCustomer, currentUser }) => {
    const { cart, totalAmount, totalItems, clearCart, deleteItem, subTotalExVat, totalVat, grandTotal } = useCart();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [assignedSalesPersonId, setAssignedSalesPersonId] = useState(() =>
        getDefaultSalesPersonId(preSelectedCustomer, currentUser)
    );

    // If a customer was pre-selected from the main page, start with it set.
    // The user can still change it from within the drawer if needed.
    const [customer, setCustomer] = useState(preSelectedCustomer || null);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Delivery scheduling
    const [deliveryDate, setDeliveryDate] = useState(() => {
        // Default to tomorrow
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    });
    const [timeSlot, setTimeSlot] = useState('');
    const [notes, setNotes] = useState('');

    // Delivery schedules from API
    const [deliverySchedules, setDeliverySchedules] = useState([]);
    const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

    // In-drawer customer search (fallback if no customer was pre-selected)
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ name: '', location: '', mobile: '', salesPersonIds: [] });
    const [salesPersons, setSalesPersons] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    const isManagerial = currentUser?.realmRoles?.some(r => ['TENANT_ADMIN', 'TENANT_OWNER', 'SALES_MANAGER', 'ADMIN'].includes(r));

    const searchTimeout = useRef(null);

    // Sync customer when the prop changes (e.g., PosView changes selected customer)
    useEffect(() => {
        setCustomer(preSelectedCustomer || null);
        setAssignedSalesPersonId(getDefaultSalesPersonId(preSelectedCustomer, currentUser));
    }, [preSelectedCustomer, currentUser]);

    // Fetch delivery schedules when drawer opens
    useEffect(() => {
        if (isOpen && deliverySchedules.length === 0) {
            setIsLoadingSchedules(true);
            getDeliverySchedules()
                .then(data => {
                    setDeliverySchedules(data || []);
                    // Pre-select the default schedule
                    const defaultSchedule = (data || []).find(s => s.default || s.isDefault);
                    if (defaultSchedule && !timeSlot) {
                        setTimeSlot(defaultSchedule.name);
                    }
                })
                .catch(err => console.error('Failed to load delivery schedules', err))
                .finally(() => setIsLoadingSchedules(false));
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset per-open state
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSearchResults([]);
            setIsAddingNew(false);
            setNotes('');
            setAssignedSalesPersonId(getDefaultSalesPersonId(customer, currentUser));
        }
    }, [isOpen, customer, currentUser]);

    // Search customers with debounce
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!searchTerm) { setSearchResults([]); return; }

        searchTimeout.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const results = await customerService.searchCustomers(searchTerm);
                setSearchResults(results);
            } catch (err) {
                console.error('Search failed', err);
            } finally {
                setIsSearching(false);
            }
        }, 300);
    }, [searchTerm]);

    // Fetch sales persons for selection
    useEffect(() => {
        if (isOpen && salesPersons.length === 0) {
            setIsLoadingUsers(true);
            getAllUsers({ size: 1000 })
                .then(data => {
                    const allUsers = data.content || data || [];
                    const filtered = allUsers.filter(u => 
                        u.realmRoles?.some(role => ['SALES_PERSON', 'SALES_MANAGER', 'TENANT_ADMIN', 'ADMIN'].includes(role.replace('ROLE_', '')))
                    );
                    setSalesPersons(filtered);
                })
                .catch(err => console.error('Failed to load users', err))
                .finally(() => setIsLoadingUsers(false));
        }
    }, [isOpen, salesPersons.length]);



    const handleAddNewCustomer = async () => {
        if (!newCustomer.name || !newCustomer.mobile) return;
        try {
            const added = await customerService.createCustomer(newCustomer);
            setCustomer({ ...newCustomer, id: added.publicId });
            setIsAddingNew(false);
            setNewCustomer({ name: '', location: '', mobile: '', salesPersonIds: [] });
            setSearchTerm('');
        } catch (err) {
            console.error('Failed to add customer', err);
        }
    };

    const handlePlaceOrder = async () => {
        if (!customer) { alert('Please select a customer first'); return; }
        if (cart.length === 0) { alert('Cart is empty'); return; }
        setIsSubmitting(true);
        try {
            await onPlaceOrder(cart, customer, assignedSalesPersonId, deliveryDate || null, timeSlot || null, notes || null);
        } catch (err) {
            console.error('[PosCartDrawer] Failed to place order:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60]"
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >

                {/* ── Header ── */}
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <h2 className="text-lg font-bold text-gray-800">Review Order</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-4 space-y-4 bg-gray-50/40">

                    {/* ── Customer Section ── */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">Customer</h3>

                        {customer ? (
                            /* Customer chip */
                            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-xl border border-blue-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                                        {(customer.name || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-900 text-sm">{customer.name}</p>
                                        {customer.mobileNumber && (
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                <Phone size={10} /> {customer.mobileNumber}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {/* Only allow changing if no customer pre-selected from main page */}
                                {!preSelectedCustomer && (
                                    <button
                                        onClick={() => setCustomer(null)}
                                        className="text-xs text-blue-600 font-semibold hover:underline"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                        ) : (
                            /* Customer search (shown only if no pre-selected customer) */
                            <div className="space-y-3">
                                {!isAddingNew ? (
                                    <>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                            <input
                                                type="text"
                                                placeholder="Search customer by name or mobile…"
                                                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        {searchTerm && (
                                            <div className="border border-gray-100 rounded-lg overflow-hidden max-h-40 overflow-y-auto bg-white shadow-sm">
                                                {searchResults.length > 0 ? (
                                                    searchResults.map(c => (
                                                        <button
                                                            key={c.id}
                                                            onClick={() => { setCustomer(c); setSearchTerm(''); }}
                                                            className="w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between border-b last:border-0"
                                                        >
                                                            <div>
                                                                <p className="font-medium text-sm text-gray-800">{c.name}</p>
                                                                <p className="text-xs text-gray-500">{c.mobileNumber}</p>
                                                            </div>
                                                            <Check size={14} className="text-gray-300" />
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="p-3 text-center text-xs text-gray-500">
                                                        {isSearching ? 'Searching…' : 'No customers found'}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setIsAddingNew(true)}
                                            className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2"
                                        >
                                            <UserPlus size={16} /> Add New Customer
                                        </button>
                                    </>
                                ) : (
                                    /* New customer form */
                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h4 className="text-sm font-medium text-gray-800">New Customer</h4>
                                            <button onClick={() => setIsAddingNew(false)}>
                                                <X size={14} className="text-gray-400 hover:text-gray-600" />
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="relative">
                                                <User className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                                <input type="text" placeholder="Name *" className="w-full pl-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    value={newCustomer.name} onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} />
                                            </div>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                                <input type="text" placeholder="Mobile Number *" className="w-full pl-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    value={newCustomer.mobile} onChange={e => setNewCustomer({ ...newCustomer, mobile: e.target.value })} />
                                            </div>
                                            <div className="relative">
                                                <MapPin className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                                <input type="text" placeholder="Location" className="w-full pl-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    value={newCustomer.location} onChange={e => setNewCustomer({ ...newCustomer, location: e.target.value })} />
                                            </div>
                                            <div className="relative">
                                                <User className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                                <select
                                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                    value={newCustomer.salesPersonIds?.[0] || ''}
                                                    onChange={e => setNewCustomer({ ...newCustomer, salesPersonIds: e.target.value ? [e.target.value] : [] })}
                                                    disabled={isLoadingUsers}
                                                >
                                                    <option value="">Sales Person (optional)</option>
                                                    {salesPersons.map(u => (
                                                        <option key={u.publicId} value={u.publicId}>{u.firstName} {u.lastName}</option>
                                                    ))}
                                                </select>
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                                    <ChevronDown size={14} className="text-gray-400" />
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleAddNewCustomer}
                                                disabled={!newCustomer.name || !newCustomer.mobile}
                                                className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Save &amp; Select
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Sales Person Section (Only for Managers/Admins) ── */}
                    {isManagerial && (
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">Assigned Sales Person</h3>
                            <div className="relative">
                                <User className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                <select
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    value={assignedSalesPersonId}
                                    onChange={e => setAssignedSalesPersonId(e.target.value)}
                                    disabled={isLoadingUsers}
                                >
                                    <option value="">Select Sales Person</option>
                                    {salesPersons.map(u => (
                                        <option key={u.publicId} value={u.publicId}>
                                            {u.firstName} {u.lastName} ({u.username})
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <ChevronDown size={14} className="text-gray-400" />
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-2">
                                The order will be credited and assigned to this staff member.
                            </p>
                        </div>
                    )}

                    {/* ── Delivery Date & Time Slot ── */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                            <Calendar size={11} /> Delivery Schedule <span className="text-gray-300 font-normal">(optional)</span>
                        </h3>
                        <div className="space-y-2.5">
                            {/* Date picker */}
                            <div>
                                <CustomDatePicker
                                    mode="single"
                                    value={deliveryDate}
                                    onChange={val => setDeliveryDate(val)}
                                    quickFilters={false}
                                    className="w-full"
                                />
                            </div>
                            {/* Time slot */}
                            <div className="relative">
                                <Clock className="absolute left-3 top-2.5 text-gray-400 w-4 h-4 pointer-events-none" />
                                <select
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    value={timeSlot}
                                    onChange={e => setTimeSlot(e.target.value)}
                                    disabled={isLoadingSchedules}
                                >
                                    {isLoadingSchedules ? (
                                        <option value="">Loading...</option>
                                    ) : deliverySchedules.length > 0 ? (
                                        deliverySchedules.map(schedule => (
                                            <option key={schedule.publicId} value={schedule.name}>
                                                {schedule.name}{schedule.startTime && schedule.endTime ? ` (${schedule.startTime} – ${schedule.endTime})` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="">Any time</option>
                                            <option value="Morning">Morning</option>
                                            <option value="Afternoon">Afternoon</option>
                                            <option value="Evening">Evening</option>
                                        </>
                                    )}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <ChevronDown size={14} className="text-gray-400" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Special Instructions ── */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider flex items-center gap-1.5">
                            <MessageSquare size={11} className="text-gray-400" /> Special Instructions <span className="text-gray-300 font-normal">(optional)</span>
                        </h3>
                        <textarea
                            className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400 min-h-[80px] resize-none"
                            placeholder="Add landmarks, specific delivery instructions or notes here..."
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            maxLength={1000}
                        />
                    </div>

                    {/* ── Cart Items ── */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Cart Items ({totalItems})</h3>
                            {cart.length > 0 && (
                                <button
                                    onClick={clearCart}
                                    className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                                >
                                    <Trash2 size={12} /> Clear all
                                </button>
                            )}
                        </div>

                        {cart.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-6">Your cart is empty</p>
                        ) : (
                            <div className="space-y-2">
                                {cart.map(item => (
                                    <div key={item.id} className="group flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0">
                                                {(item.name || '?').charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-800 text-sm truncate">{item.name}</p>
                                                <p className="text-xs text-gray-500">Qty: {item.quantity} × AED {item.price?.toFixed(2)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <p className="font-semibold text-gray-800 text-sm">AED {(item.price * item.quantity).toFixed(2)}</p>
                                            <button
                                                onClick={() => deleteItem(item.id)}
                                                className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-all opacity-0 group-hover:opacity-100"
                                                title="Remove"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <div className="pt-3 border-t space-y-1 text-xs text-gray-500 font-semibold">
                                    <div className="flex justify-between">
                                        <span>Subtotal (Excl. VAT)</span>
                                        <span>AED {subTotalExVat}</span>
                                    </div>
                                    <div className="flex justify-between pb-1.5 border-b border-dashed border-gray-150">
                                        <span>Total VAT</span>
                                        <span>AED {totalVat}</span>
                                    </div>
                                </div>
                                <div className="pt-2 flex justify-between items-center font-bold text-gray-900">
                                    <span>Total (Incl. VAT)</span>
                                    <span className="text-lg text-blue-600 font-black">AED {grandTotal}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer: Place Order ── */}
                <div className="border-t bg-white px-4 py-4 pb-safe">
                    <button
                        onClick={handlePlaceOrder}
                        disabled={isSubmitting || !customer || cart.length === 0}
                        className={`w-full py-3.5 rounded-xl font-bold text-base transition-all ${(customer && cart.length > 0 && !isSubmitting)
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            } flex items-center justify-center gap-2`}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                Processing...
                            </>
                        ) : !customer ? (
                            'Select a customer to order'
                        ) : (
                            'Place Order'
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes slide-up {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up { animation: slide-up 0.25s ease-out; }
            `}</style>
        </div>
    );
};

const ChevronDown = ({ size, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m6 9 6 6 6-6"/>
    </svg>
);

export default PosCartDrawer;
